/**
 * DOG OWNED — native cart integration for the pack builder.
 *
 * Adds a whole pack to the real Shopify basket in one request, then proves what
 * actually happened by reconciling against /cart.js — because the Release 4B
 * evidence is unambiguous: the HTTP status is not authoritative (a 422 can still
 * add), the response body cannot be trusted to enumerate every failure, and
 * mixed availability can behave in ways only the real cart reveals.
 *
 * It never clears the basket, never creates a parallel checkout, and never
 * double-adds a variant just because it helped two areas — the draft already
 * guarantees one canonical item per variant. An in-flight lock and an
 * idempotency hash defeat the double-submit race.
 */
window.CartPack = (function () {
  'use strict';

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function numericId(variantId) {
    // gid://shopify/ProductVariant/123 -> 123 ; already-numeric passes through
    return String(variantId).split('/').pop();
  }

  function hashItems(items) {
    return items
      .map(function (i) { return numericId(i.variantId) + ':' + i.quantity; })
      .sort()
      .join('|');
  }

  function cartQuantities() {
    return fetch(root() + 'cart.js', { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var byId = {};
        (cart.items || []).forEach(function (line) { byId[String(line.id)] = line.quantity; });
        return byId;
      });
  }

  var inFlightHash = null;

  /**
   * @param items [{ variantId (gid or numeric), quantity }]
   * @returns Promise<{
   *   outcome: 'success' | 'partial' | 'none' | 'busy' | 'error',
   *   results: [{ variantId, requested, delta, status: 'added'|'clamped'|'not_added' }]
   * }>
   */
  function add(items) {
    if (!items || !items.length) return Promise.resolve({ outcome: 'none', results: [] });

    var hash = hashItems(items);
    // Reject an identical submission already in flight — the double-click guard.
    if (inFlightHash === hash) return Promise.resolve({ outcome: 'busy', results: [] });
    inFlightHash = hash;

    var payload = items.map(function (i) { return { id: numericId(i.variantId), quantity: i.quantity }; });

    return cartQuantities().then(function (before) {
      return fetch(root() + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items: payload })
      })
        .then(function (res) { return res.text().then(function () { return res.status; }); })
        // Regardless of status, the real cart is the only authority. Read it and
        // compute the true per-variant delta.
        .then(function () { return cartQuantities(); })
        .then(function (after) {
          var results = items.map(function (i) {
            var id = numericId(i.variantId);
            var delta = (after[id] || 0) - (before[id] || 0);
            var status = delta >= i.quantity ? 'added' : (delta > 0 ? 'clamped' : 'not_added');
            return { variantId: i.variantId, requested: i.quantity, delta: delta, status: status };
          });
          var added = results.filter(function (r) { return r.status === 'added'; }).length;
          var touched = results.filter(function (r) { return r.status !== 'not_added'; }).length;
          var outcome = touched === 0 ? 'none' : (added === results.length ? 'success' : 'partial');
          return { outcome: outcome, results: results };
        });
    })
      .catch(function () {
        return { outcome: 'error', results: [] };
      })
      .then(function (out) {
        inFlightHash = null;
        return out;
      });
  }

  return { add: add, numericId: numericId };
})();
